import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {
  AlertThresholdDirection,
  ApiFeature,
  CategoryAlertProperties,
  ThresholdAlertProperties,
} from 'app/modules/Remote/Feature/types';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import * as C from 'app/utils/constants';

import {ThresholdSlider} from '../../Sliders';
import {AlertPreviewChart} from '../AlertPreviewChart';
import {CategorySelectorPair} from '../CategorySelectorPair';
import cs from '../styles.module.scss';
import {AlertFormAction, AlertFormState} from '../useAlertForm';

export const ThresholdAlertStep: React.FC<{
  disableInputs: boolean;
  formState: AlertFormState;
  dispatch: React.Dispatch<AlertFormAction>;
  feature: ApiFeature | undefined;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
}> = React.memo(({formState, dispatch, disableInputs, feature, featureCollection}) => {
  const isThreshold = formState.alertType === C.CUSTOM_ALERT_TYPE_THRESHOLD;
  const isCategory = formState.alertType === C.CUSTOM_ALERT_TYPE_CATEGORY;
  const categoryProps = isCategory
    ? (formState.properties as unknown as CategoryAlertProperties)
    : undefined;
  const thresholdProps = isThreshold
    ? (formState.properties as unknown as ThresholdAlertProperties)
    : undefined;

  return (
    <div className={cs.dialogPanel}>
      {feature && (
        <AlertPreviewChart
          feature={feature}
          layer={formState.layer}
          featureCollection={featureCollection}
          thresholdValue={
            formState.alertType === C.CUSTOM_ALERT_TYPE_THRESHOLD
              ? formState.properties.value
              : undefined
          }
          thresholdDirection={
            formState.alertType === C.CUSTOM_ALERT_TYPE_THRESHOLD
              ? formState.properties.direction
              : undefined
          }
          numConsecutiveScenes={
            formState.alertType === C.CUSTOM_ALERT_TYPE_THRESHOLD
              ? formState.properties.numConsecutiveScenes
              : undefined
          }
        />
      )}
      {isThreshold && thresholdProps && (
        <>
          <B.FormGroup
            label="Set threshold value"
            subLabel="Drag slider or enter a number to set your threshold."
          >
            <ThresholdSlider
              layer={formState.layer}
              thresholdValue={thresholdProps.value}
              onChangeValue={(value) => dispatch({type: 'SET_THRESHOLD_SLIDER_VALUE', value})}
              disabled={disableInputs}
            />
          </B.FormGroup>
          <B.FormGroup label="Threshold direction">
            <B.RadioGroup
              onChange={({currentTarget: {value}}) => {
                dispatch({
                  type: 'SET_THRESHOLD_DIRECTION',
                  direction: value as AlertThresholdDirection,
                });
              }}
              selectedValue={thresholdProps.direction}
              disabled={disableInputs}
            >
              <B.Radio label="Above" value="above"></B.Radio>
              <B.Radio label="Below" value="below"></B.Radio>
            </B.RadioGroup>
          </B.FormGroup>
        </>
      )}

      {isCategory &&
        'layerLegendMap' in formState.layer &&
        categoryProps &&
        formState.layer.layerLegendMap && (
          <B.FormGroup label="Categories">
            <CategorySelectorPair
              layerLegendMap={formState.layer.layerLegendMap}
              onChangeLeft={(items) =>
                dispatch({
                  type: 'SET_FROM_CATEGORIES',
                  categories: items as unknown as number[],
                })
              }
              onChangeRight={(items) =>
                dispatch({
                  type: 'SET_TO_CATEGORIES',
                  categories: items as unknown as number[],
                })
              }
              leftSelectedItems={categoryProps.fromCategories}
              rightSelectedItems={categoryProps.toCategories}
              disabled={disableInputs}
            />
          </B.FormGroup>
        )}
    </div>
  );
});
